import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAutoSave } from "@/hooks/useAutoSave";
/* ───────────────────────────────────────── schema ───────────────────────────────────────── */
const splashPageSchema = z.object({
  splash_page_title: z.string().optional(),
  splash_page_subtitle: z.string().optional(),
  start_button_text: z.string().optional(),
});

type SplashPageFormValues = z.infer<typeof splashPageSchema>;

/* ───────────────────────────────────────── props ───────────────────────────────────────── */
interface SplashPageCustomizationFormProps {
  agent: any;
  updateAgent: (data: any) => Promise<boolean>;
  onFormChange?: (values: SplashPageFormValues) => void;
}

/* ───────────────────────────────────────── component ───────────────────────────────────────── */
const SplashPageCustomizationForm = ({
  agent,
  updateAgent,
  onFormChange,
}: SplashPageCustomizationFormProps) => {
  const [initialFormValues, setInitialFormValues] =
    useState<SplashPageFormValues | null>({
      splash_page_title: agent.splash_page_title || "",
      splash_page_subtitle: agent.splash_page_subtitle || "",
      start_button_text: agent.start_button_text || "Start Chat",
    });

  /* ---------- form ---------- */
  const form = useForm<SplashPageFormValues>({
    resolver: zodResolver(splashPageSchema),
    defaultValues: {
      splash_page_title: agent.splash_page_title || "",
      splash_page_subtitle: agent.splash_page_subtitle || "",
      start_button_text: agent.start_button_text || "Start Chat",
    },
  });

  /* ---------- autosave ---------- */
  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      const data = form.getValues();
      return await updateAgent(data);
    },
    successMessage: "Splash page settings saved automatically",
    errorMessage: "Failed to auto-save splash page settings",
  });

  /* ---------- watch for live preview updates ---------- */
  const watchedValues = useWatch({
    control: form.control,
    defaultValue: {
      splash_page_title: agent.splash_page_title || "",
      splash_page_subtitle: agent.splash_page_subtitle || "",
      start_button_text: agent.start_button_text || "Start Chat",
    }
  });

  // Call onFormChange whenever watched values change
  useEffect(() => {
    if (onFormChange) {
      onFormChange(watchedValues);
    }
  }, [watchedValues, onFormChange]);

  /* ---------- watch for auto-save ---------- */
  useEffect(() => {
    const subscription = form.watch((values) => {
      // Check if there are actual changes for auto-save
      if (initialFormValues) {
        const hasChanges = Object.keys(values).some(
          (key) =>
            values[key as keyof SplashPageFormValues] !==
            initialFormValues[key as keyof SplashPageFormValues]
        );
        if (hasChanges) {
          setInitialFormValues(values);
          setDirty();
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form, setDirty, initialFormValues]);

  /* ──────────────────────────────── render ──────────────────────────────── */
  return (
    <Form {...form}>
      <form className="space-y-6">
        <FormField
          control={form.control}
          name="splash_page_title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Title
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter splash page title"
                  className="focus:border-[#060520] focus:ring-1 focus:ring-[#060520]"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <p className="text-xs text-gray-500 mt-2">
                The main title displayed on the splash page.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="splash_page_subtitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Subtitle
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter splash page subtitle"
                  className="min-h-20 focus:border-[#060520] focus:ring-1 focus:ring-[#060520]"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <p className="text-xs text-gray-500 mt-2">
                A brief description or subtitle for the splash page.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="start_button_text"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Button Text
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Start Chat"
                  className="focus:border-[#060520] focus:ring-1 focus:ring-[#060520]"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <p className="text-xs text-gray-500 mt-2">
                The text displayed on the button that starts the chat.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        {isSaving && (
          <div className="flex items-center justify-end">
            <span className="text-sm text-[#666] flex items-center">
              <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Auto-saving...
            </span>
          </div>
        )}
      </form>
    </Form>
  );
};

export default SplashPageCustomizationForm;
